import { useState, useRef, useEffect } from 'react';
import {
  Box,
  Button,
  IconButton,
  MenuItem,
  Typography,
  Drawer,
  TextField,
  InputAdornment,
  Select,
  FormControl,
  Avatar
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { Dialog, DialogTitle, DialogContent, DialogActions } from '@mui/material';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import { Alert } from "@mui/material";
import axios from '../../api/axios';
import ErrorOutline from "@mui/icons-material/ErrorOutline";

export const AddTeamUser = ({ drawerOpen, setDrawerOpen, onUserAdded }) => {

  const CREATE_USER = "/createuser/";

  const [initialValues, setInitialValues] = useState({ firstName: "", lastName: "" });
  const [formValues, setFormValues] = useState({
    firstName: "",
    lastName: "",
    email: "",
    mobile: "",
    password: "",
    confirmPassword: ""
  });

  const hasAnyData = () => {
    return (
      (formValues.firstName || "").trim() !== "" ||
      (formValues.lastName || "").trim() !== "" ||
      (formValues.email || "").trim() !== "" ||
      (formValues.mobile || "").trim() !== "" ||
      (formValues.password || "").trim() !== "" ||
      (formValues.confirmPassword || "").trim() !== ""
    );
  };

  const [emailError, setEmailError] = useState("");

  // Attempt to close drawer
  const handleCloseDrawer = () => {
    if (hasUnsavedChanges()) {
      setDialogOpen(true); // Show confirmation dialog
    } else {
      setDrawerOpen(false);
    }
  };

  // Detect if form has unsaved changes
  const hasUnsavedChanges = () => {
    return (
      (formValues.firstName || "").trim() !== (initialValues.firstName || "").trim() ||
      (formValues.lastName || "").trim() !== (initialValues.lastName || "").trim() ||
      (formValues.email || "").trim() !== (initialValues.email || "").trim() ||
      (formValues.mobile || "").trim() !== (initialValues.mobile || "").trim() ||
      (formValues.password || "").trim() !== (initialValues.password || "").trim() ||
      (formValues.confirmPassword || "").trim() !== (initialValues.confirmPassword || "").trim()
    );
  };

  // Function to format phone number for display
  const formatPhoneNumber = (phoneNumber) => {
    if (phoneNumber === null || phoneNumber === undefined || phoneNumber === '') return '';

    const phoneStr = String(phoneNumber);
    const cleaned = phoneStr.replace(/\D/g, '');

    if (cleaned.length === 10) {
      return `${cleaned.slice(0, 3)}-${cleaned.slice(3, 6)}-${cleaned.slice(6)}`;
    }
    return phoneStr;
  };

  // Function to remove formatting for storage
  const unformatPhoneNumber = (phoneNumber) => {
    if (phoneNumber === null || phoneNumber === undefined || phoneNumber === '') return '';
    const phoneStr = String(phoneNumber);
    return phoneStr.replace(/\D/g, '');
  };

  // Handle mobile number input with formatting
  const handleMobileChange = (e) => {
    const input = e.target.value;
    const cleaned = unformatPhoneNumber(input);

    // Store the raw number in formValues for API calls
    setFormValues({ ...formValues, mobile: cleaned });
  };

  const [dialogOpen, setDialogOpen] = useState(false);
  const [showFormWarning, setShowFormWarning] = useState(false);
  const warningRef = useRef(null);
  const [errors, setErrors] = useState({}); // store error messages

  const [role, setRole] = useState('user');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const validate = () => {
    let newErrors = {};

    if (!(formValues.firstName || "").trim()) {
      newErrors.firstName = "You must enter a first name.";
    }

    if (!(formValues.lastName || "").trim()) {
      newErrors.lastName = "You must enter a last name.";
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!(formValues.email || "").trim() || !emailRegex.test(formValues.email || "")) {
      newErrors.email = "You must enter a valid email address.";
    }

    if ((formValues.mobile || "") && !/^\d{10}$/.test(formValues.mobile || "")) {
      newErrors.mobile = "Mobile number is optional but must be 10 digits.";
    }

    const passwordRegex = /^(?=.*[A-Za-z])(?=.*\d).{8,}$/;
    if (!passwordRegex.test(formValues.password || "")) {
      newErrors.password = "Use at least 8 characters, with a letter and a number";
    }

    if ((formValues.password || "") !== (formValues.confirmPassword || "")) {
      newErrors.confirmPassword = "Must match with new password. Please try again.";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };


  useEffect(() => {
    if (drawerOpen) {
      setShowFormWarning(false); // clear warning when opening drawer
      setErrors({}); // also clear field errors if needed
    }
  }, [drawerOpen]);

  const handleSaveMember = async () => {
    const isValid = validate();
    if (!isValid) {
      setShowFormWarning(true);
      setTimeout(() => {
        warningRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
      }, 0);
      return;
    }

    try {
      const addUserResponse = await axios.post(CREATE_USER, {
        first: formValues.firstName,
        last: formValues.lastName,
        email: formValues.email,
        mobile: formValues.mobile?.trim(),
        role: role,
        password: formValues.password,
        rePassword: formValues.confirmPassword,
        orgId: localStorage.getItem("orgId"),
        eulaAccepted:
          "Fri Apr 04 2025 09:47:08 GMT+0200 (Central European Summer Time)",
      });

      if (addUserResponse.data.code === 0) {
        console.log("✅ User created successfully:", addUserResponse.data.user);

        if (onUserAdded) onUserAdded(addUserResponse.data.user);

        setFormValues(initialValues);
        setDrawerOpen(false);
        setShowFormWarning(false);
      } else {
        console.error("❌ API Error:", addUserResponse.data.message);
      }
    } catch (error) {
      if (error.response?.status === 409) {
        // 👇 Show validation error under email field
        setErrors((prev) => ({
          ...prev,
          email: "A member with this email already exists.",
        }));
      } else {
        console.error("Unexpected error:", error);
      }
    }

    console.log("Form submitted", formValues);
  };


  // Close drawer and dialog on confirm
  const handleConfirmClose = () => {
    setDialogOpen(false);
    setDrawerOpen(false);
    setFormValues(initialValues); // reset so no dialog on reopen
  };

  // for the blue border hovering the element values using arrow keys
  const accessListboxRef = useRef(null);

  return (
    <Drawer
      anchor="right"
      open={drawerOpen}
      onClose={(event, reason) => {
        if (reason === "backdropClick") {
          if (!hasAnyData()) {
            setDrawerOpen(false);
          }
          return;
        }
        handleCloseDrawer();
      }}
    >
      <Box
        sx={{
          width: "600px",
          height: "974px",
          display: "flex",
          flexDirection: "column",
          position: "relative",
        }}
      >
        {/* Header */}
        <Box
          sx={{
            flexShrink: 0,
            px: 3,
            py: 2,
            borderBottom: "1px solid #e0e0e0",
            position: "sticky",
            top: 0,
            bgcolor: "white",
            zIndex: 1,
          }}
        >
          <IconButton
            onClick={handleCloseDrawer}
            sx={{ position: "absolute", top: 8, right: 8 }}
          >
            <CloseIcon />
          </IconButton>
          <Typography variant="h6">Add Member</Typography>
        </Box>

        {/* Scrollable Content */}
        <Box
          sx={{
            flexGrow: 1,
            overflowY: "auto",
            px: 3,
            py: 2,
          }}
        >
          {showFormWarning && (
            <Alert
              ref={warningRef}
              severity="error"
              icon={<ErrorOutline />}
              sx={{ mb: 2, backgroundColor: "#fdecea", color: "#5f2120" }}
            >
              <strong>Missing required information</strong>
              <br />
              Please fill in the required missing information as noted.
            </Alert>
          )}

          <Box sx={{ display: "flex", justifyContent: "center", mb: 2 }}>
            <Avatar sx={{ width: 120, height: 120, bgcolor: "grey.400" }}>
              {formValues.firstName || formValues.lastName
                ? `${formValues.firstName?.[0] || ""}${formValues.lastName?.[0] || ""}`.toUpperCase()
                : "FL"}
            </Avatar>
          </Box>

          <Typography sx={{ fontWeight: "bold", mb: 2 }}>
            Personal Information
          </Typography>
          <TextField
            variant="standard"
            fullWidth
            label="First Name"
            value={formValues.firstName}
            onChange={(e) =>
              setFormValues({ ...formValues, firstName: e.target.value })
            }
            sx={{ mb: 2 }}
            error={!!errors.firstName}
            helperText={errors.firstName}
          />

          <TextField
            variant="standard"
            fullWidth
            label="Last Name"
            value={formValues.lastName}
            onChange={(e) =>
              setFormValues({ ...formValues, lastName: e.target.value })
            }
            sx={{ mb: 2 }}
            error={!!errors.lastName}
            helperText={errors.lastName}
          />

          <TextField
            variant="standard"
            fullWidth
            label="Email"
            value={formValues.email}
            onChange={(e) => {
              setFormValues({ ...formValues, email: e.target.value });
              setErrors((prev) => ({ ...prev, email: "" })); // 👈 clears email error
            }}
            sx={{ mb: 2 }}
            error={!!errors.email}
            helperText={errors.email}
          />

          <TextField
            variant="standard"
            fullWidth
            label="Mobile Number (Optional)"
            value={formatPhoneNumber(formValues.mobile)}
            onChange={handleMobileChange}
            sx={{ mb: 2 }}
            error={!!errors.mobile}
            helperText={errors.mobile}
          />

          <Typography sx={{ fontWeight: "bold", mt: 2, mb: 1 }}>Access</Typography>
          <FormControl fullWidth sx={{ mb: 2 }}>
            <Select
              variant="standard"
              value={role}
              onChange={(e) => setRole(e.target.value)}
              MenuProps={{
                  MenuListProps: {
                    ref: accessListboxRef,
                    onKeyDown: () => accessListboxRef.current?.classList.add('keyboard-nav'),
                    onMouseMove: () => accessListboxRef.current?.classList.remove('keyboard-nav'),
                  },
                  PaperProps: {
                    sx: {
                      '& .MuiMenuItem-root.Mui-selected': {
                        backgroundColor: '#1976d214 !important',
                        fontWeight: 'bold',
                      },
                      '& .MuiMenuItem-root.Mui-selected:hover': {
                        backgroundColor: '#1976d229 !important',
                        fontWeight: 'bold',
                      },
                      '& .MuiMenuItem-root:hover': {
                        backgroundColor: '#0000000a !important',
                        fontWeight: 'normal',
                      },
                      '& .keyboard-nav .MuiMenuItem-root.Mui-focusVisible': {
                        outline: '2px solid #1976d2 !important',
                        outlineOffset: '-2px',
                      },
                    },
                  },
                }}
              >
              <MenuItem value="admin">Admin</MenuItem>
              <MenuItem value="useredit">Editor</MenuItem>
              <MenuItem value="user">Viewer</MenuItem>
            </Select>
          </FormControl>

          <Typography sx={{ fontWeight: "bold", mt: 2, mb: 1 }}>Password</Typography>
          <TextField
            fullWidth
            variant="standard"
            label="Password"
            type={showPassword ? "text" : "password"}
            value={formValues.password}
            onChange={(e) =>
              setFormValues({ ...formValues, password: e.target.value })
            }
            sx={{ mb: 2 }}
            error={!!errors.password}
            helperText={
              errors.password || "Use at least 8 characters, with a letter and a number"
            }
            FormHelperTextProps={{
              sx: errors.password ? { color: "error.main" } : {},
            }}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton onClick={() => setShowPassword(!showPassword)}>
                    {showPassword ? <Visibility /> : <VisibilityOff />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />

          <TextField
            fullWidth
            variant="standard"
            label="Re-enter password"
            type={showConfirmPassword ? "text" : "password"}
            value={formValues.confirmPassword}
            onChange={(e) =>
              setFormValues({ ...formValues, confirmPassword: e.target.value })
            }
            sx={{ mb: 3 }}
            error={!!errors.confirmPassword}
            helperText={errors.confirmPassword || "Passwords must match"}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  >
                    {showConfirmPassword ? <Visibility /> : <VisibilityOff />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
        </Box>

        {/* Footer */}
        <Box
          sx={{
            flexShrink: 0,
            p: 2,
            borderTop: "1px solid #e0e0e0",
            position: "sticky",
            bottom: 0,
            bgcolor: "white",
          }}
        >
          <Button
            variant="contained"
            onClick={handleSaveMember}
          >
            ADD MEMBER
          </Button>
        </Box>

        {/* Unsaved Changes Dialog */}
        <Dialog
          open={dialogOpen}
          onClose={() => setDialogOpen(false)}
          PaperProps={{
            sx: {
              width: 600,
              height: 168,
            },
          }}
        >
          <DialogTitle>You have unsaved changes</DialogTitle>
          <DialogContent sx={{ fontSize: "16px" }}>
            Do you want to close and discard changes?
          </DialogContent>
          <DialogActions>
            <Button
              variant="contained"
              onClick={handleConfirmClose}
              color="primary"
              sx={{ marginRight: "320px" }}
            >
              Yes, Close
            </Button>
            <Button onClick={() => setDialogOpen(false)} color="primary" autoFocus>
              No, Keep Editing
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </Drawer>

  )
}